import { useGraphQLQuery } from '@betterment/graphql';
import { ErrorBoundary, createConfig, createFlowContext } from '@betterment/shared-components';
import { Outlet, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { RetirementPurposeCreationOptionDocument } from './graphql/query.retirementPurposeCreationOption.generated';

import { nameAndImage } from '../sharedSchemas';

type RetirementSelection = 'RetirementGrowth' | 'RetirementIncome';

export const retirementDetails = Yup.object({
  retirementAge: Yup.number().required('Retirement age is required'),
  retirementZipCode: Yup.string().required('Retirement zip code is required'),
});

export const desiredSpending = Yup.object({
  desiredMonthlySpending: Yup.string().required('Desired monthly spending amount is required'),
});

const RETIREMENT_OPTIONS: RetirementSelection[] = ['RetirementGrowth', 'RetirementIncome'];

export const retirementQuestionWithOptions = (options = RETIREMENT_OPTIONS) =>
  Yup.object({
    selectedRetirement: Yup.mixed<RetirementSelection>().oneOf(options).required(),
  });

const RETIREMENT_FLOW_CONFIG = createConfig({
  nameAndImage: {
    schema: nameAndImage,
    dependencies: Yup.object({}),
  },
  retirementSelection: {
    schema: retirementQuestionWithOptions(),
    dependencies: Yup.object({
      nameAndImage,
    }),
  },
  retirementDetails: {
    schema: retirementDetails,
    dependencies: Yup.object({
      retirementSelection: retirementQuestionWithOptions(['RetirementGrowth']),
    }),
  },
  desiredSpending: {
    schema: desiredSpending,
    dependencies: Yup.object({
      retirementDetails,
    }),
  },
});

export const { FlowProvider, useStep: useRetirementStep } = createFlowContext(RETIREMENT_FLOW_CONFIG);

export const RetirementFlow = () => {
  const { flowId } = useParams<{ flowId: string }>();

  const { data } = useGraphQLQuery(RetirementPurposeCreationOptionDocument, {
    throwOnError: true,
  });

  const retirementGrowthOption = data?.purposeCreationOptions.filter(
    option => option.__typename === 'RetirementGrowthPurposeCreationOption'
  )[0];

  if (!data || !retirementGrowthOption) return null;

  const { financialProfile, user } = data;

  const initialValues = {
    nameAndImage: {
      name: retirementGrowthOption.defaultName,
      imageId: retirementGrowthOption.image.id,
    },
    retirementDetails: {
      retirementAge:
        financialProfile?.retirementAssumptions?.userAssumptions.retirementAge ??
        retirementGrowthOption.defaultRetirementAge,
      retirementZipCode:
        financialProfile?.retirementAssumptions?.retirementZip ?? user?.residentialAddress?.postalCode ?? '',
    },
  };

  return (
    <FlowProvider flowId={flowId} initialValues={initialValues}>
      <ErrorBoundary fallbackType="full">
        <Outlet />
      </ErrorBoundary>
    </FlowProvider>
  );
};
